import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuthHttp } from './useHttp';

/**
 * Holly 3.0 Registration spec §6 — destination pages call this to hydrate from
 * the Holly session linked at registration time. Returns a partial session view
 * (subset matching the Holly MCP /sessions/:id response, fetched through the
 * Koa gateway at /hollyv4/session/:id).
 *
 * Source of truth for the sessionId, in priority order:
 *  1. `?hollyState=` URL param (carried from registration)
 *  2. `?hollySessionId=` URL param (alias used in some destinations)
 *  3. localStorage `holly_thread_id` (the public chat widget, AISidebar, writes
 *     the active thread id here as a plain string)
 *  4. localStorage `floxHollySessionIds[0]` (the authenticated in-app copilot
 *     writes a capped array here)
 */
export interface HollySessionView {
  sessionId: string;
  flow?: string | null;
  declaredIntent?: string | null;
  status?: string | null;
  capturedContact?: { name?: string; email?: string } | null;
  flowState?: {
    shortlistId?: string;
    quoteId?: string;
    declaredTrack?: 'warehousing' | 'transport' | 'both';
    matchedWarehouseIds?: string[];
    searchParameters?: Record<string, unknown>;
    quoteParameters?: Record<string, unknown>;
    estimateRange?: { low: number; high: number; currency: string };
    // Holly flat TransportBookingFlowState fields (holly-v4-mcp keeps v3's shape —
    // verbatim; older `quote*`/`search*` fields kept above for back-compat).
    origin?: string | null;
    destination?: string | null;
    originPostcode?: string | null;
    destinationPostcode?: string | null;
    loadSize?: {
      quantity?: number | null;
      unit?: string | null;
      description?: string | null;
    } | null;
    weightKg?: number | null;
    isPalletised?: boolean | null;
    timing?: { date?: string | null; urgency?: string | null } | null;
    specialRequirements?: string[] | null;
    estimateId?: string | null;
  } | null;
}

// The public chat widget (AISidebar) stores the active thread id as a plain
// string; the authenticated copilot stores a capped array. Read both.
const HOLLY_THREAD_ID_KEY = 'holly_thread_id';
const HOLLY_SESSION_IDS_KEY = 'floxHollySessionIds';

export function useHollySession(): {
  session: HollySessionView | null;
  loading: boolean;
  sessionId: string | null;
} {
  const [searchParams] = useSearchParams();
  const authHttp = useAuthHttp();
  const sessionId =
    searchParams.get('hollyState') ||
    searchParams.get('hollySessionId') ||
    readMostRecentSessionId();
  const [session, setSession] = useState<HollySessionView | null>(null);
  const [loading, setLoading] = useState<boolean>(!!sessionId);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    async function load() {
      try {
        const { data } = await authHttp.get<HollySessionView>(
          `/hollyv4/session/${encodeURIComponent(sessionId as string)}`,
          { baseURL: '/' }
        );
        if (!cancelled) setSession(data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          // Session expired or never existed — silently fall through; the page renders without hydration.
          return;
        }
        // Any other failure: log but don't block the page.
        // eslint-disable-next-line no-console
        console.warn('useHollySession: hydration failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return { session, loading, sessionId };
}

function readMostRecentSessionId(): string | null {
  try {
    // Public chat widget: a single thread id stored as a plain string.
    const threadId = localStorage.getItem(HOLLY_THREAD_ID_KEY);
    if (threadId) return threadId;

    // Authenticated copilot: a capped array; take the most recent.
    const raw = localStorage.getItem(HOLLY_SESSION_IDS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? String(parsed[0]) : null;
  } catch {
    return null;
  }
}
